import { useMutation, useQuery } from "@apollo/client";
import { Alert, Box, Button, CircularProgress, MenuItem, Stack, TextField, Typography } from "@mui/material";
import type { ChangeEvent, FormEvent, ReactNode } from "react";
import { useCallback, useState } from "react";
import { Title, useNotify, useTranslate } from "react-admin";

import { MarketingSendingDomain } from "../components/MarketingSendingDomain";
import { MarketingTestSend } from "../components/MarketingTestSend";
import { RequirePermission } from "../components/RequirePermission";
import { SettingsSection } from "../components/SettingsSection";
import { ResourceName } from "../constants/resources";
import {
  MarketingDeliveryProviderType,
  MarketingDeliverySettingsDocument,
  type MarketingDeliverySettingsFieldsFragment,
  type MarketingDeliverySettingsQuery,
  UpdateMarketingDeliverySettingsDocument,
  type UpdateMarketingDeliverySettingsMutation,
  type UpdateMarketingDeliverySettingsMutationVariables,
} from "../generated/graphql";
import { captureException } from "../lib/errorHandling";
import { marketingDeliveryErrorKey } from "../lib/marketingDelivery";
import { space, typography } from "../theme/designTokens";

type TextFieldChange = ChangeEvent<HTMLInputElement | HTMLTextAreaElement>;

type MarketingDeliveryFormProps = {
  settings: MarketingDeliverySettingsFieldsFragment;
};

const MarketingDeliveryForm = ({ settings }: MarketingDeliveryFormProps) => {
  const translate = useTranslate();
  const notify = useNotify();
  const [provider, setProvider] = useState(settings.provider ?? settings.availableProviders[0]?.type);
  const [apiKey, setApiKey] = useState("");
  const [apiSecret, setApiSecret] = useState("");
  const [webhookSecret, setWebhookSecret] = useState("");
  const [sendingDomain, setSendingDomain] = useState(settings.sendingDomain ?? "");
  const [fromAddress, setFromAddress] = useState(settings.fromAddress ?? "");
  const [fromName, setFromName] = useState(settings.fromName ?? "");
  const [replyToAddress, setReplyToAddress] = useState(settings.replyToAddress ?? "");
  const [error, setError] = useState<string | null>(null);
  const [update, { loading: saving }] = useMutation<
    UpdateMarketingDeliverySettingsMutation,
    UpdateMarketingDeliverySettingsMutationVariables
  >(UpdateMarketingDeliverySettingsDocument);

  const handleProvider = useCallback(
    (event: TextFieldChange) => setProvider(event.target.value as MarketingDeliveryProviderType),
    []
  );
  const handleApiKey = useCallback((event: TextFieldChange) => setApiKey(event.target.value), []);
  const handleApiSecret = useCallback((event: TextFieldChange) => setApiSecret(event.target.value), []);
  const handleWebhookSecret = useCallback((event: TextFieldChange) => setWebhookSecret(event.target.value), []);
  const handleSendingDomain = useCallback((event: TextFieldChange) => setSendingDomain(event.target.value), []);
  const handleFromAddress = useCallback((event: TextFieldChange) => setFromAddress(event.target.value), []);
  const handleFromName = useCallback((event: TextFieldChange) => setFromName(event.target.value), []);
  const handleReplyToAddress = useCallback((event: TextFieldChange) => setReplyToAddress(event.target.value), []);

  const canSave =
    !!provider && !saving && sendingDomain.trim() !== "" && fromAddress.trim() !== "" && fromName.trim() !== "";

  const handleSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      setError(null);
      if (!provider) return;
      try {
        await update({
          variables: {
            input: {
              provider,
              // An empty secret keeps the stored one; a typed one is sent as it is.
              apiKey: apiKey || null,
              apiSecret: apiSecret || null,
              webhookSecret: webhookSecret || null,
              sendingDomain: sendingDomain.trim(),
              fromAddress: fromAddress.trim(),
              fromName: fromName.trim(),
              replyToAddress: replyToAddress.trim() || null,
            },
          },
          update: (cache, { data }) => {
            if (data?.updateMarketingDeliverySettings) {
              cache.writeQuery<MarketingDeliverySettingsQuery>({
                query: MarketingDeliverySettingsDocument,
                data: { marketingDeliverySettings: data.updateMarketingDeliverySettings },
              });
            }
          },
        });
        notify(translate("marketingDelivery.saved"), { type: "success" });
      } catch (saveError) {
        const key = marketingDeliveryErrorKey(saveError);
        if (key === null) captureException(saveError, "marketing/delivery: save settings");
        setError(translate(key ?? "marketingDelivery.saveError"));
      }
    },
    [
      apiKey,
      apiSecret,
      fromAddress,
      fromName,
      notify,
      provider,
      replyToAddress,
      sendingDomain,
      translate,
      update,
      webhookSecret,
    ]
  );

  return (
    <Box component="form" noValidate onSubmit={handleSubmit}>
      <Stack gap={space.sm2}>
        <SettingsSection title={translate("marketingDelivery.provider.title")}>
          <TextField
            select
            fullWidth
            label={translate("marketingDelivery.provider.type")}
            value={provider}
            onChange={handleProvider}
          >
            {settings.availableProviders.map(({ type, name }) => (
              <MenuItem key={type} value={type}>
                {translate(`marketingDelivery.provider.types.${type}`, { _: name })}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            fullWidth
            type="password"
            autoComplete="new-password"
            label={translate("marketingDelivery.provider.apiKey")}
            helperText={settings.hasApiKey ? translate("marketingDelivery.provider.stored") : undefined}
            value={apiKey}
            onChange={handleApiKey}
          />
          <TextField
            fullWidth
            type="password"
            autoComplete="new-password"
            label={translate("marketingDelivery.provider.apiSecret")}
            helperText={settings.hasApiSecret ? translate("marketingDelivery.provider.stored") : undefined}
            value={apiSecret}
            onChange={handleApiSecret}
          />
          <TextField
            fullWidth
            type="password"
            autoComplete="new-password"
            label={translate("marketingDelivery.provider.webhookSecret")}
            helperText={
              settings.hasWebhookSecret
                ? translate("marketingDelivery.provider.stored")
                : translate("marketingDelivery.provider.webhookSecretHint")
            }
            value={webhookSecret}
            onChange={handleWebhookSecret}
          />
        </SettingsSection>

        <SettingsSection title={translate("marketingDelivery.sender.title")}>
          <TextField
            fullWidth
            required
            label={translate("marketingDelivery.sender.sendingDomain")}
            helperText={translate("marketingDelivery.sender.sendingDomainHint")}
            value={sendingDomain}
            onChange={handleSendingDomain}
          />
          <TextField
            fullWidth
            required
            label={translate("marketingDelivery.sender.fromAddress")}
            helperText={translate("marketingDelivery.sender.fromAddressHint")}
            value={fromAddress}
            onChange={handleFromAddress}
          />
          <TextField
            fullWidth
            required
            label={translate("marketingDelivery.sender.fromName")}
            value={fromName}
            onChange={handleFromName}
          />
          <TextField
            fullWidth
            label={translate("marketingDelivery.sender.replyToAddress")}
            value={replyToAddress}
            onChange={handleReplyToAddress}
          />
        </SettingsSection>

        {error && <Alert severity="error">{error}</Alert>}
        <Box>
          <Button type="submit" variant="contained" disabled={!canSave}>
            {translate("marketingDelivery.save")}
          </Button>
        </Box>
      </Stack>
    </Box>
  );
};

const MarketingDelivery = () => {
  const translate = useTranslate();
  const { data, loading, error } = useQuery<MarketingDeliverySettingsQuery>(MarketingDeliverySettingsDocument, {
    fetchPolicy: "cache-and-network",
  });
  const settings = data?.marketingDeliverySettings;

  let content: ReactNode = null;
  if (error) content = <Alert severity="error">{translate("marketingDelivery.loadError")}</Alert>;
  else if (loading && !settings) content = <CircularProgress aria-label={translate("marketingDelivery.loading")} />;
  else if (settings)
    content = (
      <>
        {/* The key resets the form, and so the typed secrets, from the saved response. */}
        <MarketingDeliveryForm key={settings.updatedAt ?? "unsaved"} settings={settings} />
        {settings.sendingDomain && (
          // The key checks the DNS again when a save changes the domain or the provider.
          <MarketingSendingDomain key={`dns-${settings.updatedAt}`} provider={settings.provider ?? null} />
        )}
        {settings.updatedAt && (
          <MarketingTestSend
            recipientsRestricted={settings.recipientsRestricted}
            allowedRecipients={settings.allowedRecipients}
          />
        )}
      </>
    );

  return (
    <Stack gap={space.sm2} maxWidth={760} data-testid="marketing-delivery-page">
      <Title title={translate("marketingDelivery.title")} />
      <Box>
        <Typography variant="h5" fontWeight={typography.fontWeight.semibold}>
          {translate("marketingDelivery.heading")}
        </Typography>
        <Typography color="text.secondary">{translate("marketingDelivery.description")}</Typography>
      </Box>
      {content}
    </Stack>
  );
};

export const MarketingDeliveryPage = () => (
  <RequirePermission permission={ResourceName.MarketingDelivery}>
    <MarketingDelivery />
  </RequirePermission>
);
